// Device-signed HTTP. Every request carries a P-256 signature over method, path, time and body.
import { canonicalBytes, sha256Hex, signBytes } from "@vavr/core";

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message?: string) { super(message ?? code); this.status = status; this.code = code; }
}

export interface DeviceIdentity { device_id: string; keys: CryptoKeyPair; api_base: string }

export class DeviceApi {
  id: DeviceIdentity;
  fetchImpl: typeof fetch;
  /** Server clock minus device clock, learned from heartbeats; used only for display. */
  skewMs = 0;
  constructor(id: DeviceIdentity, fetchImpl: typeof fetch = (...a) => fetch(...a)) { this.id = id; this.fetchImpl = fetchImpl; }

  async request(method: string, path: string, opts: { json?: unknown; bytes?: Uint8Array; type?: string; headers?: Record<string, string> } = {}): Promise<Response> {
    const body = opts.bytes ?? (opts.json === undefined ? new Uint8Array() : new TextEncoder().encode(JSON.stringify(opts.json)));
    const ts = Date.now();
    const sig = await signBytes(this.id.keys.privateKey, canonicalBytes({ m: method, p: path, t: ts, b: await sha256Hex(body) }));
    return this.fetchImpl(`${this.id.api_base}${path}`, {
      method,
      body: method === "GET" ? undefined : (body as BodyInit),
      headers: { Authorization: `PMU-Device id=${this.id.device_id},ts=${ts},sig=${sig}`, "Content-Type": opts.type ?? "application/json", ...opts.headers },
    });
  }

  async json<T = any>(method: string, path: string, opts: Parameters<DeviceApi["request"]>[2] = {}): Promise<T> {
    const res = await this.request(method, path, opts);
    const data = await res.json().catch(() => ({})) as any;
    if (!res.ok) throw new ApiError(res.status, data.error ?? "error", data.message);
    return data as T;
  }
}
